/**
 * Tag Action - WHAT Section
 *
 * Edit mode UI for adding/removing client tags.
 * Used inline within AutomationCard when editing the "What" section.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-tag.hbs
 *
 * Fields:
 * - tags: 'add' or 'remove' - the action to perform
 * - content: Array of tag names to add/remove
 */

import { useQueryClient } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AddableAutocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { useSession } from 'src/contexts/SessionContext';
import { persistNewPlatformTagListEntry } from 'src/models/tags/persistNewPlatformTagListEntry';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';

import { ActionWhatSectionProps } from '../automationActions';

type TagOption = { id: string; title: string; inputValue?: string };

/**
 * Tag WHAT Section - Edit mode
 */
export const TagWhatSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();
  const { session } = useSession();
  const queryClient = useQueryClient();

  // Get client tags from session
  const clientTags = session?.clientTags || [];
  const selectedTags = Array.isArray(automation.content)
    ? automation.content.map((tag) => String(tag).trim()).filter(Boolean)
    : [];

  const tagOptions: TagOption[] = clientTags.map((tag) => ({
    id: tag,
    title: tag,
  }));
  const allTagOptions = [...tagOptions];
  selectedTags.forEach((tag) => {
    if (!allTagOptions.some((option) => option.id === tag)) {
      allTagOptions.push({ id: tag, title: tag });
    }
  });
  const selectedOptions = allTagOptions.filter((option) =>
    selectedTags.includes(option.id)
  );

  const tagActionOptions = [
    { value: 'add', label: t('automations.tag.addTags') },
    { value: 'remove', label: t('automations.tag.removeTags') },
  ];

  return (
    <Stack spacing={2}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        {/* Add/Remove selector */}
        <Box sx={{ minWidth: 150 }}>
          <FormSelect
            name={`automations.${index}.tags`}
            options={tagActionOptions}
          />
        </Box>

        <Box>:</Box>

        {/* Tag selection */}
        <Box sx={{ minWidth: 250, flex: 1 }}>
          <AddableAutocomplete<TagOption, true>
            multiple
            onAddItem={(newTagName) =>
              persistNewPlatformTagListEntry(
                session,
                'client',
                newTagName,
                queryClient
              )
            }
            onChange={(_event, value) => {
              const nextTags = (value || []).map((option) => {
                if (typeof option === 'string') {
                  return option.trim();
                }

                return String(option.inputValue || option.id || option.title).trim();
              }).filter(Boolean);
              setFieldValue(`automations.${index}.content`, nextTags);
            }}
            options={allTagOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('automations.tag.addTagsPlaceholder')}
                size="small"
              />
            )}
            size="small"
            value={selectedOptions}
          />
        </Box>

        {/* Reset link */}
        <Box
          component="a"
          sx={{
            cursor: 'pointer',
            color: 'primary.main',
            '&:hover': { textDecoration: 'underline' },
          }}
          onClick={() => setFieldValue(`automations.${index}.content`, [])}
        >
          {t('automations.common.resetTags')}
        </Box>
      </Box>
    </Stack>
  );
};

/**
 * Tag What Summary Component
 */
export const TagWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const tags = Array.isArray(automation.content) ? automation.content : [];
  const isRemove = automation.tags === 'remove';

  if (tags.length === 0) {
    return <i>{t('automations.tag.noTagsSelected')}</i>;
  }

  return (
    <>
      {isRemove ? t('automations.tag.removeTags') : t('automations.tag.addTags')}: {tags.join(', ')}
    </>
  );
};
